// ============================================================================
// TeamOverviewCard — carte equipe onglet Apercu management (GEST CARTES METIER)
// ============================================================================

import { ChevronRight, Pencil } from 'lucide-react';
import { GsAvatar, GsProgressBar } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { TeamSummary } from '../../hooks/useTeams';
import type { AdminTeamSummary } from '../../hooks/useAdminTeams';
import {
  composePersonRoleSubtitle,
  derivePersonRole,
  personRoleI18nKey,
} from '../../lib/org/derivePersonRole';
import { orgLabelsEquivalent } from '../../lib/org/normalizeOrgLabel';
import {
  BusinessEntityCard,
  BusinessEntityCardMetaItem,
  BusinessEntityCardMetaRow,
} from '../BusinessEntityCard';
import { monthShort } from '../../pages/equipes/dates';

/** Champs communs liste General + Admin (AdminTeamSummary sans badgePulses). */
export type TeamOverviewCardTeam = Pick<
  TeamSummary,
  | 'id'
  | 'name'
  | 'department'
  | 'headcount'
  | 'directorEmail'
  | 'directorName'
  | 'memberCount'
  | 'latestMood'
  | 'latestPeriod'
  | 'currentMood'
  | 'submittedThisPeriod'
  | 'objectivesTotal'
  | 'objectivesDone'
  | 'objectivesInProgress'
>;

export interface TeamOverviewCardProps {
  team: TeamOverviewCardTeam | AdminTeamSummary;
  to: string;
  /** Edition meta admin (TeamsTab) — action distincte du lien de carte. */
  onEdit?: () => void;
  editLabel?: string;
}

export function TeamOverviewCard({ team, to, onEdit, editLabel }: TeamOverviewCardProps) {
  const { t, i18n } = useTranslation();
  const directorLabel = team.directorName ?? team.directorEmail;

  const departmentSubtitle =
    team.department && !orgLabelsEquivalent(team.name, team.department)
      ? team.department
      : undefined;

  const directorRoleLabel = team.directorEmail
    ? (() => {
        const role = derivePersonRole({ isTeamDirector: true });
        return role ? t(personRoleI18nKey(role)) : null;
      })()
    : null;
  const directorSubtitle = composePersonRoleSubtitle(directorRoleLabel, null);

  let pulseContent;
  if (team.submittedThisPeriod && team.currentMood != null) {
    pulseContent = t('gest:equipes.pulse.score', { score: team.currentMood });
  } else if (team.latestMood != null && team.latestPeriod) {
    pulseContent = (
      <span className="font-medium text-slate-400 dark:text-slate-500">
        {t('gest:equipes.pulse.lastSeen', {
          month: monthShort(i18n.language, team.latestPeriod),
          score: team.latestMood,
        })}
      </span>
    );
  } else {
    pulseContent = (
      <span className="font-medium text-slate-400 dark:text-slate-500">
        {t('gest:equipes.pulse.none')}
      </span>
    );
  }

  const headerActions = onEdit ? (
    <button
      type="button"
      aria-label={editLabel}
      onClick={onEdit}
      className="inline-flex h-8 w-8 shrink-0 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    >
      <Pencil size={16} aria-hidden="true" />
    </button>
  ) : undefined;

  return (
    <BusinessEntityCard
      title={team.name}
      titleClassName="text-head font-bold text-slate-900 dark:text-slate-100"
      subtitle={departmentSubtitle}
      headerClassName="-mx-4 -mt-4 rounded-t-xl border-b border-brand-500/10 bg-brand-500/6 px-4 py-3 dark:border-brand-400/15 dark:bg-brand-500/10"
      badge={
        <div className="flex items-center gap-1.5">
          <span
            className={`rounded-full px-2 py-0.5 text-chip font-medium ${
              team.submittedThisPeriod
                ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            {team.submittedThisPeriod
              ? t('gest:equipes.pulse.submitted')
              : t('gest:equipes.pulse.pending')}
          </span>
          <ChevronRight
            size={16}
            aria-hidden="true"
            className="text-slate-400 dark:text-slate-500"
          />
        </div>
      }
      headerActions={headerActions}
      activateLabel={t('gest:equipes.cards.openTeam', { name: team.name })}
      to={to}
      meta={
        <BusinessEntityCardMetaRow>
          <BusinessEntityCardMetaItem label={t('gest:equipes.columns.members')}>
            {team.memberCount || team.headcount}
          </BusinessEntityCardMetaItem>
          <BusinessEntityCardMetaItem label={t('gest:equipes.columns.pulse')}>
            {pulseContent}
          </BusinessEntityCardMetaItem>
          <BusinessEntityCardMetaItem label={t('gest:equipes.columns.objectives')}>
            {team.objectivesTotal > 0 ? (
              <GsProgressBar
                value={team.objectivesDone}
                max={team.objectivesTotal}
                label
                className="w-full"
              />
            ) : (
              <span className="font-medium text-slate-400 dark:text-slate-500">—</span>
            )}
          </BusinessEntityCardMetaItem>
        </BusinessEntityCardMetaRow>
      }
    >
      <div className="flex min-h-10 items-center gap-2.5">
        {directorLabel ? (
          <>
            <GsAvatar name={directorLabel} size="sm" />
            <div className="min-w-0">
              <p className="truncate text-field font-medium text-slate-800 dark:text-slate-100">
                {directorLabel}
              </p>
              {directorSubtitle ? (
                <p className="truncate text-meta text-slate-500 dark:text-slate-400">
                  {directorSubtitle}
                </p>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <span
              aria-hidden="true"
              className="h-8 w-8 shrink-0 rounded-full border border-dashed border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800"
            />
            <p className="text-field font-medium text-slate-500 dark:text-slate-400">
              {t('gest:equipes.noDirector')}
            </p>
          </>
        )}
      </div>
    </BusinessEntityCard>
  );
}
